import React, { useEffect, useState } from "react";
import LayoutNav from "containers/LayoutNav";
import { connect, useDispatch, useSelector } from "react-redux";

import { resetLayoutsToEmpty } from "store/actions/layout.js";
import { updateSidePanelAction } from "store/actions/sidepanel.js";
import { setPageLayouts } from "store/thunk/layout";
import useAppInsightsHandler from "hooks/useAppInsightsHandler.js";
import useParamsHandler from "hooks/useLayoutHandler.js";

import DashboardAPIs from "api/dashboard.js";

import { extractSystemId, getLayout, sortWidgetsBasedOnOrder } from "utils/utils.js";
import { DELETE_DIALOG_STRINGS, DeleteWidgetStrings } from "constants/constantStrings";
import { SYSTEMS_PAGES } from "constants/pages.constant";

import DupontDialog from "components/DupontDialog";
import EditWidgetModal from "components/EditWidgetModal";
import { MAX_GRID_COLS_DASHBOARD } from "components/GridLayout/GridConstants";
import PageLoading, { OverlayLoader } from "components/PageLoading";

import DashboardMain from "./DashboardMain";
import { calculateCoordinates, calculateLinearIndex, getGridWidth } from "./DashboardUtils";
import NoItemsLayout from "./NoItemsLayout";

import "./index.scss";

const Dashboard = props => {
  const dispatch = useDispatch();
  const { isDashboardPanelOpened } = useSelector(state => state.sidePanelData);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isDefaultDashboard, setIsDefaultDashboard] = useState(true);
  const [dashboardTags, setDashboardTags] = useState([]);
  const [dashboardMainWidgets, setDashboardMainWidgets] = useState([]);
  const [isRerendering, setIsRerendering] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isAddWidgetFormOpen, setIsAddWidgetFormOpen] = useState(props.isLeftSideNavOpen);
  const [isAddLayoutFormOpen, setIsAddLayoutFormOpen] = useState(false);
  const [isEditLayoutFormOpen, setIsEditLayoutFormOpen] = useState(false);
  const [isWidgetEditing, setIsWidgetEditing] = useState(false);
  const [editWidgetData, setEditWidgetData] = useState(null);
  const [editWidgetId, setEditWidgetId] = useState(null);
  const [deleteWidgetId, setDeleteWidgetId] = useState(null);
  const [isWidgetDeleteDlgOpen, setIsWidgetDeleteDlgOpen] = useState(false);
  const [currentWidgetType, setCurrentWidgetType] = useState(null);
  const [activeLayout, setActiveLayout] = useState(null);
  const [isToReorder, setIsToReorder] = useState(true);

  const { getSystemId, getCurrentLayout } = useParamsHandler();

  const page = SYSTEMS_PAGES.DASHBOARDS;
  const systemID = extractSystemId();
  const objAppInsights = useAppInsightsHandler();

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const response = await DashboardAPIs.getDashboard();
        const { Layouts } = response.data;
        if (Layouts.length) {
          const { defaultLayout, layouts } = getCurrentLayout(Layouts);
          props.onSetPageLayouts(layouts, defaultLayout, page);
        } else {
          setIsLoaded(true);
          setIsRerendering(false);
        }
      } catch (err) {
        console.error("Get Dashboard error: ", err);
        setIsLoaded(true);
        setIsRerendering(false);
      }
    };
    fetchInitialData();
    return () => {
      dispatch(resetLayoutsToEmpty());
    };
  }, []);

  useEffect(() => {
    if (Object.keys(props.currentLayout).length) {
      fetchData();
    } else {
      setDashboardMainWidgets([]);
    }
    setIsToReorder(true);
  }, [isRerendering, props.currentLayout]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    const currentLayout = getLayout(props.currentLayout);
    if (!activeLayout || (currentLayout && activeLayout !== currentLayout.ID)) {
      setIsUpdating(true);
      setActiveLayout(currentLayout?.ID);
      try {
        const response = await DashboardAPIs.getDashboard(currentLayout);
        const data = response && response.data;
        if (response.status >= 200 && response.status < 300) {
          const { Widgets } = data;
          if (Widgets && Widgets.length >= 1) {
            setIsDefaultDashboard(data.DefaultDashboard);
            const orderedWidgets = sortWidgetsBasedOnOrder(Widgets);
            setDashboardMainWidgets(orderedWidgets);
          } else {
            setDashboardMainWidgets([]);
          }
        }
        if (!isLoaded) {
          setIsLoaded(true);
        }
        if (isRerendering) {
          setIsRerendering(false);
        }
      } catch (err) {
        console.log(err);
        setDashboardMainWidgets([]);
      } finally {
        setIsRerendering(false);
        setIsUpdating(false);
      }
    }
    setIsRerendering(false);
  };

  const onToggleCollapsedNav = () => {
    const isSidePanelOpened = !isDashboardPanelOpened && !isWidgetEditing;
    setIsWidgetEditing(false);
    dispatch(updateSidePanelAction({ isDashboardPanelOpened: isSidePanelOpened }));
    setIsAddLayoutFormOpen(false);
    setIsAddWidgetFormOpen(false);
    setIsEditLayoutFormOpen(false);
    setEditWidgetData(null);
  };

  const getItemIdxId = (items, id) => items.findIndex(item => item.Id === id);

  const onWidgetAction = (actionType, widgetId, widgetType, payload = null) => {
    if (widgetType) setCurrentWidgetType(widgetType);
    switch (actionType) {
      case "EDIT_WIDGET":
        setIsWidgetEditing(true);
        onEditWidget(widgetId, widgetType);
        break;
      case "DELETE_WIDGET":
        setIsWidgetDeleteDlgOpen(true);
        setDeleteWidgetId(widgetId, widgetType);
        break;
      case "ADD_WIDGET":
        addWidget(payload);
        break;
      //Download Action
      default:
        break;
    }
  };

  const addWidget = async payload => {
    try {
      objAppInsights.trackEvent("addWidget");
      if (dashboardMainWidgets.length === 0) {
        payload = { ...payload, Order: 0 };
      } else {
        const { Order, Type } = dashboardMainWidgets[dashboardMainWidgets.length - 1];
        let { x, y } = calculateCoordinates(Order);
        if (x + getGridWidth(Type) + getGridWidth(payload.Type) <= MAX_GRID_COLS_DASHBOARD) {
          x += getGridWidth(Type);
        } else {
          x = 0;
          y = y + 1;
        }
        payload = { ...payload, Order: calculateLinearIndex(y, x) };
      }
      const respData = await DashboardAPIs.addDashboardWidget(payload);
      const widgetData = respData;
      const newList = [...dashboardMainWidgets, widgetData];
      setDashboardMainWidgets(sortWidgetsBasedOnOrder(newList));
      setTimeout(() => scrollWidgetsToBottom());
    } catch {
    } finally {
      setIsUpdating(false);
    }
  };

  const scrollWidgetsToBottom = id => {
    const element = document.querySelector(".dashboard__widgets--container");
    if (element) element.scrollTop = element.scrollHeight;
  };

  const onDeleteDlgClose = async data => {
    objAppInsights.trackEvent("deleteWidget");
    setIsWidgetDeleteDlgOpen(false);
    if (data === "CONFIRM") {
      try {
        showItemUpdateLoader(deleteWidgetId);
        await DashboardAPIs.deleteDashboardWidget(props.currentLayout[systemID][page].ID, deleteWidgetId);
        const filteredWidgets = dashboardMainWidgets.filter(widget => widget.Id !== deleteWidgetId);
        setDashboardMainWidgets(filteredWidgets);
        const filteredTags = dashboardTags.filter(widget => widget.Id !== deleteWidgetId);
        setDashboardTags(filteredTags);
        if (deleteWidgetId === editWidgetId) {
          onEditWidgetClose(false);
        }

        setDeleteWidgetId(null);
      } catch (error) {
        const items = [...dashboardMainWidgets];
        const widgetIdx = getItemIdxId(items, deleteWidgetId);
        items[widgetIdx].isUpdating = false;
        setDashboardMainWidgets(items);
      }
    }
  };

  const onEditWidget = (widgetId, type) => {
    objAppInsights.trackEvent("editWidgetClick");
    setEditWidgetId(widgetId);
    const items = [...dashboardMainWidgets];
    const widgetIdx = getItemIdxId(items, widgetId);
    const data = { ...items[widgetIdx], tagsList: props.tagsList, isEdit: true, page };
    setEditWidgetData({ ...data, currentLayout: props.currentLayout[systemID][page] });
  };

  const onEditWidgetClose = data => {
    setIsWidgetEditing(false);
    if (data && typeof data === "object") updateWidget(data);
    setEditWidgetData(null);
  };

  const showItemUpdateLoader = widgetId => {
    const items = [...dashboardMainWidgets];
    const widgetIdx = getItemIdxId(items, widgetId);
    items[widgetIdx].isUpdating = true;
    setDashboardMainWidgets(items);
  };

  const updateWidget = async payload => {
    objAppInsights.trackEvent("updateWidget");
    showItemUpdateLoader(editWidgetId);
    const items = [...dashboardMainWidgets];
    const widgetIdx = getItemIdxId(items, editWidgetId);
    try {
      const data = await DashboardAPIs.updateDashboardWidget(payload);
      const updatedWidgetData = data;
      items[widgetIdx] = updatedWidgetData;
    } catch {
      items[widgetIdx].isUpdating = false;
    } finally {
      setDashboardMainWidgets(items);
    }
  };

  const getDeleteWidgetType = () => {
    const items = [...dashboardMainWidgets];
    const deleteWdigetIdx = getItemIdxId(items, deleteWidgetId);
    const itemToDelete = items[deleteWdigetIdx];
    return itemToDelete?.Header || itemToDelete?.Type;
  };

  const getDeleteDlgTitle = () => {
    const name = getDeleteWidgetType();
    return DeleteWidgetStrings.title.replace("%", name);
  };

  const getDeleteDlgDesc = () => {
    const name = props.currentLayout[systemID][page].Name;
    return DeleteWidgetStrings.description.replace("%", name);
  };

  const isDashboardWidgets = () =>
    props.currentLayout && Object.keys(props.currentLayout).length !== 0 && (dashboardMainWidgets.length || dashboardTags.length);
  const isLoadingView = () => !isLoaded;

  const getCurrentLayoutName = () => {
    let name = "";
    if (props.currentLayout) {
      const layout = props.currentLayout[systemID];
      if (layout && layout[page]) {
        name = layout[page].Name;
      }
    }
    return name;
  };

  return (
    <>
      <div className="sideNav height--100 dashboard">
        {isLoadingView() ? (
          <PageLoading />
        ) : (
          <div className="sideNavMainContainer height--100">
            {(isRerendering || isUpdating || props.showOverlayLoader) && <OverlayLoader />}
            <div
              className={`sideNavMenubutton 
              ${
                isDashboardPanelOpened || isWidgetEditing
                  ? "sideNavOpenMenuwidth sideNavCloseMenuButton"
                  : "sideNavCloseMenuwidth sideNavOpenMenuButton"
              }`}
              onClick={onToggleCollapsedNav}
            />
            {isDashboardPanelOpened && (
              <div className="sideNavMenuOpen">
                <LayoutNav
                  page={SYSTEMS_PAGES.DASHBOARDS}
                  className="sideNavMenuContainer"
                  systemID={systemID}
                  setIsDashboardRerendering={setIsRerendering}
                  tagsList={props.tagsList}
                  isLeftSideNavOpen={isDashboardPanelOpened}
                  onToggleCollapsedNav={onToggleCollapsedNav}
                  setIsAddWidgetFormOpen={setIsAddWidgetFormOpen}
                  isAddWidgetFormOpen={isAddWidgetFormOpen}
                  setIsAddLayoutFormOpen={setIsAddLayoutFormOpen}
                  isAddLayoutFormOpen={isAddLayoutFormOpen}
                  setIsEditLayoutFormOpen={setIsEditLayoutFormOpen}
                  isEditLayoutFormOpen={isEditLayoutFormOpen}
                  onWidgetAction={onWidgetAction}
                  dashboardMainWidgets={dashboardMainWidgets}
                />
              </div>
            )}
            <div className={`height--100 sideNavContainer ${isWidgetEditing && !isDashboardPanelOpened ? "side-menu-opened" : null}`}>
              {isDashboardWidgets() ? (
                <>
                  <div className="dashboard__widgets--container">
                    <DashboardMain
                      dashboardMainWidgets={dashboardMainWidgets}
                      tagsList={props.tagsList}
                      currentLayout={props.currentLayout[systemID][page].Name}
                      currentLayoutID={props.currentLayout[systemID][page].ID}
                      setDashboardMainWidgets={setDashboardMainWidgets}
                      setIsRerendering={setIsRerendering}
                      onWidgetAction={onWidgetAction}
                      isRendering={isRerendering}
                      isDashboardPanelOpened={isDashboardPanelOpened}
                      isToReorder={isToReorder}
                      setIsToReorder={setIsToReorder}
                    />
                  </div>
                </>
              ) : (
                <NoItemsLayout currentLayout={getCurrentLayoutName()} page={page} />
              )}
            </div>
          </div>
        )}
      </div>
      {isWidgetDeleteDlgOpen && (
        <DupontDialog
          open={isWidgetDeleteDlgOpen}
          type="primary"
          title={getDeleteDlgTitle()}
          cancelBtnText={DELETE_DIALOG_STRINGS.actionNo}
          confirmBtnText={DELETE_DIALOG_STRINGS.actionYes}
          onClose={onDeleteDlgClose}
        >
          {getDeleteDlgDesc()}
        </DupontDialog>
      )}
      {editWidgetData && <EditWidgetModal data={editWidgetData} onEditWidgetClose={onEditWidgetClose} />}
    </>
  );
};

const mapStateToProps = ({ tags, layout, dashboard, header, poll }) => ({
  systemsStatus: poll.systemsStatus,
  tagsList: tags.tags,
  tagsAsObj: tags.tagsAsObj,
  currentLayout: layout.currentLayouts,
  showOverlayLoader: layout.showShowOverlayLoader
});

const mapDispatchToProps = dispatch => ({
  onSetPageLayouts: (layouts, newLayout, page) => dispatch(setPageLayouts(layouts, newLayout, page))
});

export default connect(mapStateToProps, mapDispatchToProps)(Dashboard);
